import React, { useMemo, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

// Controls
import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Box
} from '@mui/material';

// Components
import { Loading } from "@gpstrackit/gtc-react-controls";
import SeverityInfoBubble from '../../severityInfoBubble';
import GadgetDateInfo from '../gadgetDateInfo';

// Helpers
import { generateColorPalette } from 'Core/data/Helpers';

// Actions
import { getGadgetData } from '../../dashboard/redux/slice';

// Chart Components
import { Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from 'chart.js';

// Styles
import './index.scss';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const PieGadget = ({
    gadgetkey,
    name,
    filters,
    config = {},
    configHandler,
    customFilters,
    showLegend = true
}) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const { gadgetsData } = useSelector(state => state.dashboardRedux);

    const gadgetData = gadgetsData[gadgetkey] ? gadgetsData[gadgetkey] : { loading: false, data: { items: [], total: 0 } };
    const { loading, data } = gadgetData;

    // Severity InfoBubble state
    const [showSeverityInfo, setShowSeverityInfo] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });

    // Memoize filters to avoid unnecessary re-renders when reference changes but content is the same
    const memoizedFilters = useMemo(() => filters, [JSON.stringify(filters)]);

    // Process data for chart
    const processedData = useMemo(() => {
        try {
            const displayData = data?.items || [];

            // Use dummy data if no data is provided or it's empty
            const dataToUse = (displayData && Array.isArray(displayData) && displayData.length > 0) ? displayData : [];

            if (!dataToUse || !Array.isArray(dataToUse) || dataToUse.length === 0) {
                return [];
            }

            // Check if the items already have colors
            const hasColors = dataToUse.some(item => item?.color);

            // Only generate colors if they don't come in the data
            const dynamicHexColors = hasColors ? [] : generateColorPalette(dataToUse.length);

            return dataToUse.map((item, index) => {
                try {
                    const rawValue = item?.eventPercent || 0;
                    const numericValue = typeof rawValue === 'number' ? rawValue : parseFloat(rawValue) || 0;
                    return {
                        name: item?.nameEvent || '',
                        value: isNaN(numericValue) ? 0 : parseFloat(numericValue.toFixed(2)),
                        color: item?.color || dynamicHexColors[index] || '#cccccc',
                        severityCritical: item?.severityCritical || 0,
                        severityHigh: item?.severityHigh || 0,
                        severityMedium: item?.severityMedium || 0,
                        severityLow: item?.severityLow || 0
                    };
                } catch (error) {
                    return {
                        name: 'Unknown',
                        value: 0,
                        color: '#cccccc'
                    };
                }
            });
        } catch (error) {
            return [];
        }
    }, [data]);

    useEffect(() => {
        dispatch(getGadgetData({ gadgetkey, page: 1, pageSize: 25, filters: memoizedFilters }));
    }, [memoizedFilters])

    // Handle wedge click - show InfoBubble with inline severity data
    const handleWedgeClick = (event, elements) => {
        if (!elements || elements.length === 0 || !processedData) return;
        const index = elements[0].index;
        const clickedEvent = processedData[index];
        if (!clickedEvent) return;

        setSelectedEvent(clickedEvent.name);
        setClickPosition({ x: event.native.clientX, y: event.native.clientY });
        setShowSeverityInfo(true);
    };

    const chartData = {
        labels: processedData.map(item => messages[`driver_performance_${item.name}`] || item.name),
        datasets: [
            {
                data: processedData.map(item => item.value),
                backgroundColor: processedData.map(item => item.color),
                borderColor: processedData.map(item => item.color),
                borderWidth: 2,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false, // We'll use custom legend if showLegend is true
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const suffix = config.tooltipSuffix || '%';
                        const value = parseFloat(context.parsed).toFixed(2);
                        return `${messages[`driver_performance_${context.label}`] || context.label}: ${value}${suffix}`;
                    }
                }
            }
        },
        cutout: config.cutout || 0, // 0 for pie chart, percentage for donut chart
        ...config.chartOptions,
        onClick: handleWedgeClick
    };

    return (
        <Card className="pie-gadget-card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHeader
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Typography variant="h6" component="div">
                            {messages[name] || name}
                        </Typography>
                        <GadgetDateInfo filters={filters} gadgetkey={gadgetkey} />
                    </div>
                }
            />
            <CardContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                        <Loading show />
                    </Box>
                ) : processedData.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                        <div style={{ color: '#8b8b8b' }}>
                            {messages['risk_no_data'] || 'No data'}
                        </div>
                    </Box>
                ) : (
                    <div className="pie-gadget-container">
                        {/* Pie Chart */}
                        <div className="chart-container no-drag" style={{ cursor: 'pointer' }}>
                            <Pie data={chartData} options={chartOptions} />
                        </div>
                        {/* Legend */}
                        {showLegend && (
                            <div className="legend-container">
                                <List dense>
                                    {processedData.map((item, index) => {
                                        let name = item?.name || ''
                                        return (
                                            <ListItem key={index}>
                                                <ListItemIcon>
                                                    <div
                                                        className="color-indicator"
                                                        style={{ backgroundColor: item?.color }}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body2">
                                                            {parseFloat(item?.value).toFixed(1)}{config?.valuesSuffix || '%'} {messages[`driver_performance_${name?.replace(/\s+/g, "")}`] || name}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                        )
                                    })}
                                </List>
                            </div>
                        )}
                        {/* Severity InfoBubble */}
                        <SeverityInfoBubble
                            open={showSeverityInfo}
                            eventName={selectedEvent}
                            position={clickPosition}
                            data={{
                                critical: processedData.find(d => d.name === selectedEvent)?.severityCritical || 0,
                                high: processedData.find(d => d.name === selectedEvent)?.severityHigh || 0,
                                medium: processedData.find(d => d.name === selectedEvent)?.severityMedium || 0,
                                low: processedData.find(d => d.name === selectedEvent)?.severityLow || 0
                            }}
                            onClose={() => setShowSeverityInfo(false)}
                        />
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default PieGadget;